import { useLDClient } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useReducer, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  CurrentUser,
  fetchCurrentUser,
  selectAuthErrorMessage,
  selectAuthIsError,
  selectAuthIsFetching,
  selectAuthIsSuccess,
  selectCurrentUser,
  selectCurrentUserToken,
  setFeatures,
  setLDIdentified
} from "features/auth/auth-slice";
import {
  BootstrapFailureRecord,
  isBootstrapBlocked,
  recordBootstrapFailure
} from "hooks/bootstrap-retry.util";
import { useLazyGetCurrentUserPaywallFeaturesQuery } from "services/paywall";
import IntercomSingleton, {
  buildIntercomIdentifyPayload
} from "utils/intercom.util";
import { buildStableLdContext } from "utils/launchdarkly-context.util";
import UserbackSingleton from "utils/userback.util";
import { viteEnv } from "utils/vite-env.util";

// Module-level flags to prevent duplicate fetchCurrentUser calls across all hook instances
let isFetchingUser = false;
let lastFetchedTokenModule: string | null = null;

// ENG-1745: replaces the old set-once `fetchFailedForToken` latch, which
// permanently blocked every re-dispatch for a token once fetchCurrentUser
// exhausted its inner retries — a transient network blip (packet loss, a
// hung socket) became a PERMANENT dead end for the rest of the SPA session.
// Refresh didn't help: the token is (correctly) preserved, so a reload
// re-reads the same token and re-triggers the same latch.
//
// This record tracks a bounded, exponentially-backed-off auto-retry instead
// — see hooks/bootstrap-retry.util.ts for the schedule and the ceiling.
let bootstrapFailureRecord: BootstrapFailureRecord | null = null;
let retryWakeTimeoutId: ReturnType<typeof setTimeout> | null = null;
// Every mounted useAuth() instance registers its re-render trigger here so
// the single module-level retry timer can wake all of them once its backoff
// delay elapses (mirrors the isFetchingUser/lastFetchedTokenModule dedup
// pattern above — Layout, administration, and leads-ui all mount this hook).
const retryWakeListeners = new Set<() => void>();

const clearRetryWakeTimer = (): void => {
  if (retryWakeTimeoutId) {
    clearTimeout(retryWakeTimeoutId);
    retryWakeTimeoutId = null;
  }
};

const scheduleRetryWake = (delayMs: number): void => {
  clearRetryWakeTimer();
  if (!Number.isFinite(delayMs)) return; // past the ceiling — manual retry only
  retryWakeTimeoutId = setTimeout(() => {
    retryWakeTimeoutId = null;
    retryWakeListeners.forEach((notify) => notify());
  }, delayMs);
};

export type UseAuthResult = {
  // Consecutive bootstrap failures for the current token (0 = healthy).
  bootstrapFailureCount: number;
  // True once the auto-retry ceiling has been hit — only a manual
  // retryBootstrap() call will attempt again.
  bootstrapBlocked: boolean;
  // The message the failed fetchCurrentUser dispatch set, if any.
  bootstrapErrorMessage: string;
  // Clears the failure record and forces an immediate retry, bypassing any
  // remaining backoff wait. Safe to call at any time.
  retryBootstrap: () => void;
};

export default function useAuth(): UseAuthResult {
  const ldClient = useLDClient();
  const intercom = IntercomSingleton.getInstance();
  const userback = UserbackSingleton.getInstance();
  const [queryPaywallFeatures] = useLazyGetCurrentUserPaywallFeaturesQuery();
  const token = useAppSelector(selectCurrentUserToken);
  const user = useAppSelector(selectCurrentUser);
  const isFetching = useAppSelector(selectAuthIsFetching);
  const isSuccess = useAppSelector(selectAuthIsSuccess);
  const isError = useAppSelector(selectAuthIsError);
  const bootstrapErrorMessage = useAppSelector(selectAuthErrorMessage);

  const dispatch = useAppDispatch();
  const lastFetchedTokenRef = useRef<string | null>(null);
  // The counter itself (not just the setter) has to be a dependency of the
  // fetch effect below — calling the setter alone re-renders this component
  // but does NOT re-run an effect whose deps are unchanged, so without
  // `retryTick` in that effect's array the scheduled backoff wake would fire
  // and do nothing.
  const [retryTick, forceRerender] = useReducer((c) => c + 1, 0);

  // Wake this instance when the module-level retry timer fires elsewhere.
  useEffect(() => {
    retryWakeListeners.add(forceRerender);
    return () => {
      retryWakeListeners.delete(forceRerender);
    };
  }, [forceRerender]);

  useEffect(() => {
    const isBlocked =
      !!token && isBootstrapBlocked(bootstrapFailureRecord, token, Date.now());

    // Only fetch if:
    // 1. We have a token
    // 2. Not already being fetched by another instance (module-level flag)
    // 3. Haven't successfully loaded yet OR token has changed
    // 4. Not currently backing off (or past the auto-retry ceiling) for this token
    if (
      token &&
      !isFetchingUser &&
      (!isSuccess || token !== lastFetchedTokenModule) &&
      !isBlocked
    ) {
      isFetchingUser = true;
      lastFetchedTokenModule = token;
      lastFetchedTokenRef.current = token;

      dispatch(fetchCurrentUser({ token }))
        .unwrap()
        .then(() => {
          bootstrapFailureRecord = null;
          clearRetryWakeTimer();
        })
        .catch(() => {
          bootstrapFailureRecord = recordBootstrapFailure(
            token,
            bootstrapFailureRecord,
            Date.now()
          );
          scheduleRetryWake(bootstrapFailureRecord.nextRetryAt - Date.now());
          forceRerender();
        })
        .finally(() => {
          isFetchingUser = false;
        });
    }
  }, [token, isFetching, isSuccess, isError, dispatch, retryTick]);

  // Manual escape hatch: clears the failure record and forces the effect
  // above to re-evaluate immediately, whether or not the auto-retry ceiling
  // has been hit. A subsequent failure starts a fresh backoff schedule.
  const retryBootstrap = useCallback(() => {
    if (bootstrapFailureRecord?.token === token) {
      bootstrapFailureRecord = null;
    }
    clearRetryWakeTimer();
    forceRerender();
  }, [token, forceRerender]);

  useEffect(() => {
    const loginUser = async (userKey: string, user: CurrentUser) => {
      try {
        // ENG-1813: the context doubles as the SDK's localStorage cache key, so
        // it has to be byte-stable across page loads. buildStableLdContext owns
        // that — do not inline attributes here.
        await ldClient?.identify(buildStableLdContext(userKey, user));
        dispatch(setLDIdentified(true));
      } catch (e) {
        console.log("ld identify failure:", e);
        dispatch(setLDIdentified(false));
      }
    };
    if (user.id !== -1) {
      const name = `${user.first_name.trim()} ${user.last_name.trim()}`.trim();
      const userKey = user.id;

      queryPaywallFeatures().then((resp) => {
        if ("data" in resp) {
          dispatch(setFeatures(resp.data));
        } else {
          console.log("Error getting paywall features:", resp.error);
        }
      });
      loginUser(userKey.toString(), user);
      // ENG-1526: payload construction (incl. the no-company rule for internal Suno staff) lives in
      // buildIntercomIdentifyPayload so it stays unit-testable.
      intercom.identify(
        buildIntercomIdentifyPayload(user, viteEnv.VITE_ENV || "local")
      );

      // Initialize Userback with user data
      userback.identify({
        id: user.id.toString(),
        name: name.trim(),
        email: user.email
      });
    }
  }, [user, ldClient, dispatch, intercom, userback, queryPaywallFeatures]);

  const failureRecord =
    bootstrapFailureRecord?.token === token ? bootstrapFailureRecord : null;

  return {
    bootstrapFailureCount: failureRecord?.failureCount ?? 0,
    // Derived straight from nextRetryAt (the value the fetch effect's own
    // isBootstrapBlocked check uses) rather than re-deriving from
    // failureCount + MAX_AUTO_RETRY_ATTEMPTS — a second computation of "are
    // we past the ceiling" could drift from the one that actually gates
    // retries.
    bootstrapBlocked: failureRecord
      ? !Number.isFinite(failureRecord.nextRetryAt)
      : false,
    bootstrapErrorMessage,
    retryBootstrap
  };
}
